import { createApi } from '@reduxjs/toolkit/query/react';
import { FabricationType } from '@/types/fabricationType';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';

type args = {
  getMachines: { active?: boolean } | void;
  postMachine: { body: FabricationType.MachineUpsert };
  patchMachine: {
    machineId: number;
    body: FabricationType.MachineUpsert;
  };
  deleteMachine: { machineId: number };

  getProcesses: { active?: boolean } | void;
  postProcess: { body: FabricationType.ProcessUpsert };
  patchProcess: {
    processId: number;
    body: FabricationType.ProcessUpsert;
  };
  deleteProcess: { processId: number };

  getProcessSteps: { processId: number };
  postProcessStep: {
    processId: number;
    body: FabricationType.ProcessStepUpsert;
  };
  patchProcessStep: {
    stepId: number;
    body: Partial<FabricationType.ProcessStepUpsert>;
  };
  deleteProcessStep: { stepId: number };

  getReleaseSummary: { projectId: number; releaseId: number };
  getReleaseSettings: { projectId: number; releaseId: number };
  patchReleaseSettings: {
    projectId: number;
    releaseId: number;
    body: FabricationType.ReleaseSettingsPatch;
  };
  getWorkOrderCandidates: { projectId: number; releaseId: number };
  patchSummaryConfig: {
    projectId: number;
    releaseId: number;
    partId: number;
    body: FabricationType.SummaryConfigPatch;
  };
  manualCompleteParts: {
    projectId: number;
    releaseId: number;
    body: { items: FabricationType.ManualCompletionItem[] };
  };

  getWorkOrders: { projectId: number; releaseId: number };
  postWorkOrders: {
    projectId: number;
    releaseId: number;
    body: FabricationType.WorkOrderCreateItem[];
  };
  patchWorkOrderStep: {
    projectId: number;
    workOrderId: number;
    stepId: number;
    body: {
      assigned_user_ids?: number[];
      machine_id?: number | null;
      status?: string;
    };
  };
  getWorkOrderById: { projectId: number; workOrderId: number };
  patchWorkOrder: {
    projectId: number;
    workOrderId: number;
    body: Partial<
      Pick<
        FabricationType.WorkOrder,
        'identifier' | 'description' | 'due_date' | 'status' | 'preferred_machine'
      >
    > & {
      assigned_user_ids?: number[];
    };
  };
  deleteWorkOrder: { projectId: number; workOrderId: number; releaseId?: number };
  bulkPatchWorkOrders: { body: FabricationType.WorkOrderBulkPatch };

  getAssignedWorkOrders: { status?: string } | void;
  executeWorkOrderStep: {
    projectId: number;
    workOrderId: number;
    body: FabricationType.ExecutionInput;
  };
  getExecutionLogs: { projectId: number; workOrderId?: number };
  getInventoryMovements: { projectId: number; releaseId?: number };
  getInventorySummary: { projectId: number };
  postInventoryBackfill: { projectId: number; body?: { release_id?: number } };
};

type responses = {
  getMachines: FabricationType.Machine[];
  postMachine: FabricationType.Machine;
  patchMachine: FabricationType.Machine;
  deleteMachine: void;

  getProcesses: FabricationType.Process[];
  postProcess: FabricationType.Process;
  patchProcess: FabricationType.Process;
  deleteProcess: void;

  getProcessSteps: FabricationType.ProcessStep[];
  postProcessStep: FabricationType.ProcessStep;
  patchProcessStep: FabricationType.ProcessStep;
  deleteProcessStep: void;

  getReleaseSummary: FabricationType.SummaryRow[];
  getReleaseSettings: FabricationType.ReleaseSettings;
  patchReleaseSettings: FabricationType.ReleaseSettings;
  getWorkOrderCandidates: FabricationType.WorkOrderCandidate[];
  patchSummaryConfig: FabricationType.FabricationConfig;
  manualCompleteParts: FabricationType.ManualCompletionResponse;

  getWorkOrders: FabricationType.WorkOrder[];
  postWorkOrders: FabricationType.WorkOrder[];
  getWorkOrderById: FabricationType.WorkOrder;
  patchWorkOrder: FabricationType.WorkOrder;
  deleteWorkOrder: void;
  bulkPatchWorkOrders: FabricationType.WorkOrder[];
  patchWorkOrderStep: FabricationType.WorkOrderStep;
  getAssignedWorkOrders: FabricationType.WorkOrder[];
  executeWorkOrderStep: FabricationType.ExecutionLog;
  getExecutionLogs: FabricationType.ExecutionLog[];
  getInventoryMovements: FabricationType.InventoryMovement[];
  getInventorySummary: FabricationType.InventorySummaryRow[];
  postInventoryBackfill: {
    detail: string;
    synced_work_orders: number;
    quantity_added: number;
  };
};

export const fabricationApi = createApi({
  reducerPath: 'fabricationApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'FabricationMachines',
    'FabricationProcesses',
    'FabricationSummary',
    'FabricationCandidates',
    'FabricationWorkOrders',
    'FabricationExecutionLogs',
    'FabricationInventoryMovements',
    'FabricationInventorySummary',
  ],
  endpoints: (builder) => ({
    getMachines: builder.query<responses['getMachines'], args['getMachines']>({
      query: (arg) => ({
        url: `/fabrication/machines/`,
        method: 'GET',
        params:
          arg && 'active' in arg && arg.active !== undefined
            ? { active: arg.active }
            : undefined,
      }),
      keepUnusedDataFor,
      providesTags: () => [{ type: 'FabricationMachines', id: 'global' }],
    }),

    postMachine: builder.mutation<responses['postMachine'], args['postMachine']>({
      query: ({ body }) => ({
        url: `/fabrication/machines/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'FabricationMachines', id: 'global' }],
    }),

    patchMachine: builder.mutation<responses['patchMachine'], args['patchMachine']>({
      query: ({ machineId, body }) => ({
        url: `/fabrication/machines/${machineId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'FabricationMachines', id: 'global' }],
    }),

    deleteMachine: builder.mutation<responses['deleteMachine'], args['deleteMachine']>({
      query: ({ machineId }) => ({
        url: `/fabrication/machines/${machineId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'FabricationMachines', id: 'global' }],
    }),

    getProcesses: builder.query<responses['getProcesses'], args['getProcesses']>({
      query: (arg) => ({
        url: `/fabrication/processes/`,
        method: 'GET',
        params:
          arg && 'active' in arg && arg.active !== undefined
            ? { active: arg.active }
            : undefined,
      }),
      keepUnusedDataFor,
      providesTags: () => [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    postProcess: builder.mutation<responses['postProcess'], args['postProcess']>({
      query: ({ body }) => ({
        url: `/fabrication/processes/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    patchProcess: builder.mutation<responses['patchProcess'], args['patchProcess']>({
      query: ({ processId, body }) => ({
        url: `/fabrication/processes/${processId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    deleteProcess: builder.mutation<responses['deleteProcess'], args['deleteProcess']>({
      query: ({ processId }) => ({
        url: `/fabrication/processes/${processId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    getProcessSteps: builder.query<responses['getProcessSteps'], args['getProcessSteps']>({
      query: ({ processId }) => ({
        url: `/fabrication/processes/${processId}/steps/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: () => [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    postProcessStep: builder.mutation<responses['postProcessStep'], args['postProcessStep']>({
      query: ({ processId, body }) => ({
        url: `/fabrication/processes/${processId}/steps/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    patchProcessStep: builder.mutation<responses['patchProcessStep'], args['patchProcessStep']>({
      query: ({ stepId, body }) => ({
        url: `/fabrication/process-steps/${stepId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    deleteProcessStep: builder.mutation<responses['deleteProcessStep'], args['deleteProcessStep']>({
      query: ({ stepId }) => ({
        url: `/fabrication/process-steps/${stepId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'FabricationProcesses', id: 'global' }],
    }),

    getReleaseSummary: builder.query<responses['getReleaseSummary'], args['getReleaseSummary']>({
      query: ({ projectId, releaseId }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/summary/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationSummary', id: `release-${arg.releaseId}` },
      ],
    }),

    getReleaseSettings: builder.query<
      responses['getReleaseSettings'],
      args['getReleaseSettings']
    >({
      query: ({ projectId, releaseId }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/settings/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationSummary', id: `settings-${arg.releaseId}` },
      ],
    }),

    patchReleaseSettings: builder.mutation<
      responses['patchReleaseSettings'],
      args['patchReleaseSettings']
    >({
      query: ({ projectId, releaseId, body }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/settings/`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ projectId, releaseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedSettings } = await queryFulfilled;
          dispatch(
            fabricationApi.util.updateQueryData(
              'getReleaseSettings',
              { projectId, releaseId },
              () => updatedSettings,
            ),
          );
        } catch {
          // PATCH failed — leave the existing cache untouched
        }
      },
    }),

    getWorkOrderCandidates: builder.query<
      responses['getWorkOrderCandidates'],
      args['getWorkOrderCandidates']
    >({
      query: ({ projectId, releaseId }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/work-order-candidates/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationCandidates', id: `release-${arg.releaseId}` },
      ],
    }),

    patchSummaryConfig: builder.mutation<
      responses['patchSummaryConfig'],
      args['patchSummaryConfig']
    >({
      query: ({ projectId, releaseId, partId, body }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/summary/${partId}/config/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationSummary', id: `release-${arg.releaseId}` },
        { type: 'FabricationCandidates', id: `release-${arg.releaseId}` },
        'FabricationWorkOrders',
      ],
    }),

    manualCompleteParts: builder.mutation<
      responses['manualCompleteParts'],
      args['manualCompleteParts']
    >({
      query: ({ projectId, releaseId, body }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/summary/manual-complete/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationSummary', id: `release-${arg.releaseId}` },
        { type: 'FabricationCandidates', id: `release-${arg.releaseId}` },
        { type: 'FabricationWorkOrders', id: `release-${arg.releaseId}` },
        { type: 'FabricationExecutionLogs', id: `project-${arg.projectId}` },
        { type: 'FabricationInventoryMovements', id: `project-${arg.projectId}` },
        { type: 'FabricationInventorySummary', id: `project-${arg.projectId}` },
        'FabricationWorkOrders',
        'FabricationSummary',
      ],
    }),

    patchWorkOrderStep: builder.mutation<
    responses['patchWorkOrderStep'],
    args['patchWorkOrderStep']
    >({
    query: ({ projectId, workOrderId, stepId, body }) => ({
        url: `/fabrication/projects/${projectId}/work-orders/${workOrderId}/steps/${stepId}/`,
        method: 'PATCH',
        body,
    }),
    invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationWorkOrders', id: `work-order-${arg.workOrderId}` },
        'FabricationWorkOrders',
        { type: 'FabricationExecutionLogs', id: `work-order-${arg.workOrderId}` },
    ],
    }),

    getWorkOrders: builder.query<responses['getWorkOrders'], args['getWorkOrders']>({
      query: ({ projectId, releaseId }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/work-orders/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationWorkOrders', id: `release-${arg.releaseId}` },
      ],
    }),

    postWorkOrders: builder.mutation<responses['postWorkOrders'], args['postWorkOrders']>({
      query: ({ projectId, releaseId, body }) => ({
        url: `/fabrication/projects/${projectId}/releases/${releaseId}/work-orders/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationWorkOrders', id: `release-${arg.releaseId}` },
        { type: 'FabricationSummary', id: `release-${arg.releaseId}` },
        { type: 'FabricationCandidates', id: `release-${arg.releaseId}` },
      ],
    }),

    getWorkOrderById: builder.query<responses['getWorkOrderById'], args['getWorkOrderById']>({
      query: ({ projectId, workOrderId }) => ({
        url: `/fabrication/projects/${projectId}/work-orders/${workOrderId}/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationWorkOrders', id: `work-order-${arg.workOrderId}` },
      ],
    }),

    patchWorkOrder: builder.mutation<responses['patchWorkOrder'], args['patchWorkOrder']>({
      query: ({ projectId, workOrderId, body }) => ({
        url: `/fabrication/projects/${projectId}/work-orders/${workOrderId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationWorkOrders', id: `work-order-${arg.workOrderId}` },
        { type: 'FabricationExecutionLogs', id: `work-order-${arg.workOrderId}` },
        'FabricationWorkOrders',
      ],
    }),

    deleteWorkOrder: builder.mutation<responses['deleteWorkOrder'], args['deleteWorkOrder']>({
      query: ({ projectId, workOrderId }) => ({
        url: `/fabrication/projects/${projectId}/work-orders/${workOrderId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        'FabricationWorkOrders',
        'FabricationSummary',
        'FabricationCandidates',
        ...(arg.releaseId
          ? [
              { type: 'FabricationWorkOrders' as const, id: `release-${arg.releaseId}` },
              { type: 'FabricationSummary' as const, id: `release-${arg.releaseId}` },
              { type: 'FabricationCandidates' as const, id: `release-${arg.releaseId}` },
            ]
          : []),
      ],
    }),

    bulkPatchWorkOrders: builder.mutation<
      responses['bulkPatchWorkOrders'],
      args['bulkPatchWorkOrders']
    >({
      query: ({ body }) => ({
        url: '/fabrication/work-orders/bulk-patch/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['FabricationWorkOrders'],
    }),

    getAssignedWorkOrders: builder.query<
      responses['getAssignedWorkOrders'],
      args['getAssignedWorkOrders']
    >({
      query: (arg) => ({
        url: '/fabrication/work-orders/assigned/',
        method: 'GET',
        params:
          arg && 'status' in arg && arg.status
            ? { status: arg.status }
            : undefined,
      }),
      keepUnusedDataFor,
      providesTags: () => [{ type: 'FabricationWorkOrders', id: 'assigned' }],
    }),

    executeWorkOrderStep: builder.mutation<
      responses['executeWorkOrderStep'],
      args['executeWorkOrderStep']
    >({
      query: ({ projectId, workOrderId, body }) => ({
        url: `/fabrication/projects/${projectId}/work-orders/${workOrderId}/execute-step/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationWorkOrders', id: `work-order-${arg.workOrderId}` },
        { type: 'FabricationExecutionLogs', id: `work-order-${arg.workOrderId}` },
        { type: 'FabricationInventoryMovements', id: `project-${arg.projectId}` },
        { type: 'FabricationInventorySummary', id: `project-${arg.projectId}` },
        'FabricationSummary',
      ],
    }),

    getExecutionLogs: builder.query<responses['getExecutionLogs'], args['getExecutionLogs']>({
      query: ({ projectId, workOrderId }) => ({
        url: `/fabrication/projects/${projectId}/execution-logs/`,
        method: 'GET',
        params: workOrderId ? { work_order_id: workOrderId } : undefined,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationExecutionLogs', id: `project-${arg.projectId}` },
        ...(arg.workOrderId
          ? [
              {
                type: 'FabricationExecutionLogs' as const,
                id: `work-order-${arg.workOrderId}`,
              },
            ]
          : []),
      ],
    }),

    getReleasePartProgress: builder.query<
      FabricationType.PartProgress,
      { projectId: number; releaseId: number; partId: number }
    >({
      query: ({ projectId, releaseId, partId }) =>
        `/fabrication/projects/${projectId}/releases/${releaseId}/parts/${partId}/progress/`,
    }),

    getInventoryMovements: builder.query<
      responses['getInventoryMovements'],
      args['getInventoryMovements']
    >({
      query: ({ projectId, releaseId }) => ({
        url: `/fabrication/projects/${projectId}/inventory-movements/`,
        method: 'GET',
        params: releaseId ? { release_id: releaseId } : undefined,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationInventoryMovements', id: `project-${arg.projectId}` },
      ],
    }),

    getInventorySummary: builder.query<
      responses['getInventorySummary'],
      args['getInventorySummary']
    >({
      query: ({ projectId }) => ({
        url: `/fabrication/projects/${projectId}/inventory-summary/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, arg) => [
        { type: 'FabricationInventorySummary', id: `project-${arg.projectId}` },
      ],
    }),

    postInventoryBackfill: builder.mutation<
      responses['postInventoryBackfill'],
      args['postInventoryBackfill']
    >({
      query: ({ projectId, body }) => ({
        url: `/fabrication/projects/${projectId}/inventory-backfill/`,
        method: 'POST',
        body: body ?? {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FabricationInventorySummary', id: `project-${arg.projectId}` },
        { type: 'FabricationInventoryMovements', id: `project-${arg.projectId}` },
        'FabricationWorkOrders',
        'FabricationSummary',
      ],
    }),
  }),
});

export const {
  useGetMachinesQuery,
  usePostMachineMutation,
  usePatchMachineMutation,
  useDeleteMachineMutation,
  useGetProcessesQuery,
  usePostProcessMutation,
  usePatchProcessMutation,
  useDeleteProcessMutation,
  useGetProcessStepsQuery,
  usePostProcessStepMutation,
  usePatchProcessStepMutation,
  useDeleteProcessStepMutation,
  useGetReleaseSummaryQuery,
  useGetReleaseSettingsQuery,
  usePatchReleaseSettingsMutation,
  useGetWorkOrderCandidatesQuery,
  usePatchSummaryConfigMutation,
  useManualCompletePartsMutation,
  useGetWorkOrdersQuery,
  useGetReleasePartProgressQuery,
  usePostWorkOrdersMutation,
  useGetWorkOrderByIdQuery,
  usePatchWorkOrderMutation,
  useDeleteWorkOrderMutation,
  useBulkPatchWorkOrdersMutation,
  useGetAssignedWorkOrdersQuery,
  useExecuteWorkOrderStepMutation,
  useGetExecutionLogsQuery,
  useGetInventoryMovementsQuery,
  usePatchWorkOrderStepMutation,
  useGetInventorySummaryQuery,
  usePostInventoryBackfillMutation,
} = fabricationApi;

export namespace FabricationApi {
  export const Api = fabricationApi;
  export type Args = args;
  export type Responses = responses;
}